import { useState, useCallback } from 'react';

/**
 * @param steps - Array of step objects
 * @returns Stepper state and navigation functions
 *
 * @example
 * ```tsx
 * const steps = [
 *   { id: 'step1', title: 'Step 1', component: Step1Component },
 *   { id: 'step2', title: 'Step 2', component: Step2Component },
 *   { id: 'step3', title: 'Step 3', component: Step3Component },
 * ];
 */
export const useStepper = <T>(steps: T[]) => {
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const [touchedSteps, setTouchedSteps] = useState<Set<number>>(new Set());

  const currentStep = steps[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  // Mark a step as touched
  const markStepAsTouched = useCallback(() => {
    setTouchedSteps((prev) => new Set(Array.from(prev).concat(currentStepIndex)));
  }, [currentStepIndex]);

  // Go to the next step
  const nextStep = useCallback(() => {
    if (!isLastStep) {
      markStepAsTouched();
      setCurrentStepIndex(currentStepIndex + 1);
    }
  }, [currentStepIndex, isLastStep, markStepAsTouched]);

  // Go to the previous step
  const previousStep = useCallback(() => {
    if (!isFirstStep) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  }, [currentStepIndex, isFirstStep]);

  // Go to a specific step
  const goToStep = useCallback(
    (stepIndex: number) => {
      if (stepIndex >= 0 && stepIndex < steps.length) {
        setCurrentStepIndex(stepIndex);
      }
    },
    [steps.length],
  );

  // Reset the stepper
  const reset = useCallback(() => {
    setCurrentStepIndex(0);
    setTouchedSteps(new Set());
  }, []);

  return {
    // Current state
    currentStep,
    currentStepIndex,
    isFirstStep,
    isLastStep,
    totalSteps: steps.length,

    // Navigation
    nextStep,
    previousStep,
    goToStep,
    reset,

    // Touch tracking
    markStepAsTouched,
    isStepTouched: (stepIndex: number) => touchedSteps.has(stepIndex),
    touchedSteps,
  };
};
